import { Box, Flex } from '@chakra-ui/react';
import { useState } from 'react';
import {
  KSteps,
  KStepItem,
} from '@/ui-library/components/molecules/KSteps/KSteps';

interface TabConfig {
  id: string;
  label: string;
  component: React.ComponentType<any>;
  props?: Record<string, any>;
}

interface SurveyTabsProps {
  tabs: TabConfig[];
  isCreateFlow?: boolean;
  onTabChange?: (tabId: string) => void;
  onTabValidation?: (fromTab: string, toTab: string) => Promise<boolean>;
  initialTab?: string;
  currentTab?: string;
  activity?: any;
  scheduling?: {
    handleSchedule: (scheduleValues: any) => void;
    handleCancelSchedule: () => void;
    isScheduling: boolean;
    isCanceling: boolean;
    scheduleRef: any;
  };
}

export const SurveyTabs = ({
  tabs,
  isCreateFlow = false,
  onTabChange,
  onTabValidation,
  initialTab = 'details',
  currentTab: externalCurrentTab,
  activity,
  scheduling,
}: SurveyTabsProps) => {
  const [internalCurrentTab, setInternalCurrentTab] = useState(initialTab);
  const [tabVisited, setTabVisited] = useState([initialTab]);

  const currentTab = externalCurrentTab || internalCurrentTab;
  const setCurrentTab = onTabChange || setInternalCurrentTab;

  const handleTabClick = async (step: KStepItem, index: number) => {
    const tabId = tabs[index].id;

    // Validate current tab before switching
    if (onTabValidation) {
      const isValid = await onTabValidation(currentTab, tabId);
      if (!isValid) return;
    }

    if (isCreateFlow && !tabVisited?.includes(tabId)) {
      const currentIndex = tabs.findIndex((t) => t.id === currentTab);
      const targetIndex = tabs.findIndex((t) => t.id === tabId);
      // Only allow moving to the next sequential tab
      if (targetIndex !== currentIndex + 1) return;
    }

    if (!tabVisited?.includes(tabId)) {
      setTabVisited((prev) => [...prev, tabId]);
    }

    setCurrentTab(tabId);
  };

  // Convert tabs to KSteps format
  const steps: KStepItem[] = tabs.map((tab, index) => {
    const currentIndex = tabs.findIndex((t) => t.id === currentTab);
    let status: 'active' | 'pending' | 'completed' = 'pending';

    if (index < currentIndex) {
      status = 'completed';
    } else if (index === currentIndex) {
      status = 'active';
    }

    // In create flow, enable visited tabs, current tab, and the next sequential tab
    const isCurrentTab = index === currentIndex;
    const isNextTab = index === currentIndex + 1;
    const isEnabled = isCreateFlow
      ? tabVisited.includes(tab.id) || isCurrentTab || isNextTab
      : true;

    return {
      id: tab.id,
      label: tab.label,
      status,
      enabled: isEnabled,
    };
  });

  const currentTabIndex = tabs.findIndex((tab) => tab.id === currentTab);

  return (
    <Flex flexDir="column" flex="1" minH="0">
      <Box
        px="24px"
        pt="20px"
        pb="16px"
        borderBottom="1px solid"
        borderColor="border-color"
      >
        <KSteps
          steps={steps}
          onSelect={({ step, index }) => handleTabClick(step, index)}
        />
      </Box>
      <Box flex="1" minH="0" overflowY="auto" overflowX="hidden">
        {tabs.map((tab, index) => {
          const Component = tab.component;
          const isActive = currentTabIndex === index;
          return (
            <Box key={tab.id} display={isActive ? 'block' : 'none'}>
              <Component
                {...tab.props}
                activity={activity}
                scheduling={scheduling}
              />
            </Box>
          );
        })}
      </Box>
    </Flex>
  );
};
